/**
 * Custom element <bundle-builder> for creating product bundles.
 *
 * Core Responsibilities:
 *  - Render and manage bundle option slots
 *  - Enforce per-step, per-item, and global selection limits
 *  - Calculate dynamic pricing with per-slot discounts
 *  - Validate bundles with Recharge APIs (fixed & dynamic)
 *  - Add bundles to Shopify cart (fixed & dynamic flows)
 *
 * Example Usage:
 *  <bundle-builder data-product-id="12345" data-variant-id="67890"></bundle-builder>
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    // Debug
    this.debug = false;

    // Afterpay
    this.afterpayInitialized = false;

    // Per-step quantity tracking
    this.stepQuantities = {};

    // Tracks the last total number of selected products we emitted a modal open event for.
    // Prevents firing duplicate `bundle-modal:open` events when the count hasn't changed.
    this.lastTotalCount = 0;

    // Bundle state
    this.bundle = {
      externalProductId: null,
      externalVariantId: null,
      selections: [],
    };
  }

  /**
   * Lifecycle hook called when the component is added to the DOM.
   * Initializes bundle settings, step quantity limits, quantity controls,
   * and event bindings.
   */
  async connectedCallback() {
    this.debug = this.dataset.debugMode === 'true';
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle;

    // Wire up bundle IDs
    this.bundle.externalProductId = this.productId;
    this.bundle.externalVariantId = this.variantId;

    // Afterpay flags/prices
    this.productPrice = parseInt(this.dataset.productPrice, 10) || 0;
    this.hasAfterPay = 'hasAfterpay' in this.dataset;

    // Safe JSON parse for slotDiscounts
    let discounts = {};
    if (this.dataset.slotDiscounts) {
      try {
        discounts = JSON.parse(this.dataset.slotDiscounts);
      } catch (err) {
        this.debugLog('[connectedCallback] invalid slotDiscounts JSON:', err);
      }
    }
    this.slotDiscounts = discounts;

    // Load translations
    const t = window.bundle_builder.translations;
    this.translations = {
      select: t.select,
      option: t.option,
      options: t.options,
      add_to_cart: t.add_to_cart,
      save: t.save,
      click_to_remove_selection: t.click_to_remove_selection,
      ...[
        t.one,
        t.two,
        t.three,
        t.four,
        t.five,
        t.six,
        t.seven,
        t.eight,
        t.nine,
      ].reduce((acc, val, idx) => ((acc[idx] = val), acc), {}),
    };

    // Media query listener
    this.mediaQuery = window.matchMedia('(min-width: 1024px)');

    // Fetch the Recharge bundle settings from CDN
    const bundleSettings = await window.recharge.cdn.getCDNBundleSettings(
      this.productId,
    );

    this.debugLog('Loaded bundle settings:', bundleSettings);

    const maxPerItem = bundleSettings?.max_quantity_per_variant;
    const variant = bundleSettings?.variants?.[0];
    const range = variant?.ranges?.[0] || {};

    // Determine if bundle is fixed-price or dynamic
    this.priceRuleType = bundleSettings?.price_rule;
    this.debugLog('Bundle price rule type:', this.priceRuleType);

    // Set global selection constraints based on price rule
    this.maxSelection =
      this.priceRuleType === 'fixed'
        ? variant?.items_count || 0
        : range.quantity_max || 0;
    this.minSelection =
      this.priceRuleType === 'fixed'
        ? this.maxSelection
        : range.quantity_min || this.maxSelection;

    this.debugLog('Global selection limits:', {
      maxSelection: this.maxSelection,
      minSelection: this.minSelection,
      maxPerItem,
    });

    // Initialize quantity limits for each step (collection)
    this.setupStepQuantities(variant?.option_sources);

    // Set up quantity input controls based on maxPerItem
    if (maxPerItem > 1) {
      this.setupQuantityInputs(maxPerItem);
    } else {
      this.removeAllQuantityInputs();
    }

    // Cache and setup key DOM elements (buttons, containers, etc.)
    this.cacheCoreElements();

    // Initialize layout and behaviors
    this.setupResponsiveSelectorPositioning();
    this.setupMobileOptionSticky();
    this.renderSelectionSlots(this.maxSelection);
    this.initializeProductSelection();
    this.updateBundleActionButton();
  }

  /**
   * Calculates the total number of products selected across all steps.
   * @returns {number} Sum of current selections in every step.
   */
  getTotalSelected() {
    return Object.values(this.stepQuantities).reduce(
      (acc, step) => acc + step.current,
      0,
    );
  }

  /**
   * Emits a 'bundle-modal:open' event if the total selected count has changed
   * since the last check. This allows external listeners (like bundle modals)
   * to respond to the user hitting an exact selection milestone (nth product).
   */
  emitNthModalIfChanged() {
    const total = this.getTotalSelected();

    if (total !== this.lastTotalCount) {
      this.lastTotalCount = total;

      // Trigger the modal associated with this nth value.
      window.dispatchEvent(
        new CustomEvent('bundle-modal:open', { detail: { nth: total } }),
      );
    }
  }

  /**
   * Initializes the Afterpay widget if enabled for this bundle.
   *
   * Checks for the presence of the 'data-has-afterpay' flag and the
   * global Afterpay constructor before bootstrapping. Targets the
   * '.bundle-builder__selector-atc' element to display the split-pay UI.
   *
   * @param {number} amountCents
   *   The total amount (in cents) to pass to Afterpay for installment calculation.
   */
  initAfterpay(amountCents) {
    if (!this.hasAfterPay || typeof window.presentAfterpay !== 'function') {
      this.debugLog('[Afterpay] failed to initialize');
      return;
    }

    // purge old widget
    this.clearAfterpayWidget();

    const config = {
      priceSelector: '.bundle-builder__selector-atc',
      locale: 'en_US',
      currency: 'USD',
      afterpayLogoColor: 'black',
      showUpperLimit: false,
      replaceModalOpenIcon: 'More Info',
      amount: amountCents,
      minMaxThreshold: { min: 3500, max: 100000 },
    };

    new window.presentAfterpay(config).init();
    this.debugLog('[Afterpay] initialized for', amountCents);
  }

  /**
   * Removes any previously injected Afterpay nodes under the CTA,
   * so that multiple inits don’t stack widgets.
   */
  clearAfterpayWidget() {
    const elements = this.querySelectorAll('.afterpay-paragraph');
    elements.forEach((el) => el.remove());
    this.debugLog('[Afterpay] cleared previous widget');
  }

  /**
   * Initializes quantity limits (min/max) for each product step
   * using data from Recharge bundle settings.
   *
   * @param {Array} optionSources - Array of source objects, each mapping to a step.
   */
  setupStepQuantities(optionSources = []) {
    optionSources.forEach((source) => {
      const step = this.querySelector(
        `.bundle-builder__step[data-collection-id='${source?.collection_id}']`,
      );
      if (!step || !source.collection_id) {
        return;
      }

      const stepId = source.collection_id;
      const max = source.quantity_max ?? null;
      const min = source.quantity_min ?? 0;

      this.stepQuantities[stepId] = { current: 0, max, min };
      this.debugLog(`[Step ${stepId}] Initialized`, { max, min });
    });
  }

  /**
   * Configures quantity input controls across all steps.
   * Only runs if maxPerItem is greater than 1.
   *
   * @param {number} maxPerItem - Max quantity allowed per product variant.
   */
  setupQuantityInputs(maxPerItem) {
    const steps = this.querySelectorAll('.bundle-builder__step');

    steps.forEach((step) => {
      step
        .querySelectorAll('.bundle-builder__product-quantity')
        .forEach((el) => {
          const input = el.querySelector('input');
          if (input) {
            input.max = maxPerItem;
          }
        });
    });

    this.productQuantityInputs = this.querySelectorAll(
      '.bundle-builder__product-quantity input',
    );
    this.productDecreaseQtyButtons = this.querySelectorAll(
      '.bundle-builder__product-quantity .minus',
    );
    this.productIncreaseQtyButtons = this.querySelectorAll(
      '.bundle-builder__product-quantity .plus',
    );
    this.initializeQuantityControls();
    this.debugLog('Quantity inputs initialized');
  }

  /**
   * Removes all quantity input controls from the DOM.
   * Used when maxPerItem is 1 or undefined.
   */
  removeAllQuantityInputs() {
    this.querySelectorAll('.bundle-builder__product-quantity').forEach((el) =>
      el.remove(),
    );
    this.debugLog('Quantity inputs removed (maxPerItem <= 1)');
  }

  /**
   * Caches and binds core interactive DOM elements:
   * - Cart button (with event listener)
   * - Subscription options container
   * - Add-to-bundle buttons
   */
  cacheCoreElements() {
    this.cartBtn = this.querySelector('.bundle-builder__selector-atc');
    this.cartBtnText = this.cartBtn.querySelector(
      '.bundle-builder__selector-atc-text',
    );
    this.handleCartButtonClick = this.handleCartButtonClick.bind(this);
    this.cartBtn.addEventListener('click', this.handleCartButtonClick);

    // Selector Wrapper
    this.selectorWrapper = this.querySelector('.bundle-builder__selector');

    this.subscriptionOptions = this.querySelector(
      '.selling_plan_app_container',
    );
    this.subscriptionOneTimeDisplay =
      this.subscriptionOptions?.querySelector(
        '.product-purchasing-option.one-time-purchase label .product-purchasing-option__price',
      ) || null;

    this.subscriptionSubInput =
      this.subscriptionOptions?.querySelector(
        '.product-purchasing-option.subscription-purchase input',
      ) || null;

    this.subscriptionSubDisplay =
      this.subscriptionOptions?.querySelector(
        '.product-purchasing-option.subscription-purchase label .product-purchasing-option__price',
      ) || null;

    // Parse subscription discount % from its input dataset
    const subPct = this.subscriptionSubInput?.dataset?.discountPercentage;
    this.subscriptionDiscount =
      subPct != null && !isNaN(parseFloat(subPct)) ? parseFloat(subPct) : 0;

    this.addToBundleButtons = this.querySelectorAll(
      '.bundle-builder__product-cta:not(:disabled)',
    );

    // Keep CTA price live with OTP/Sub toggles
    if (this.subscriptionOptions) {
      this.subscriptionOptions.addEventListener('change', () => {
        const isValid =
          this.selectorWrapper.classList.contains('is-valid') &&
          !this.cartBtn.hasAttribute('disabled');
        const labelText = (
          this.cartBtnText?.textContent ||
          this.cartBtnText?.innerText ||
          ''
        ).trim();
        const labelIsAddToCart = labelText.startsWith(
          this.translations.add_to_cart,
        );
        if (isValid && labelIsAddToCart) {
          const priceForCta = this.getActiveCtaPriceCents();
          this.setCartButtonText(this.translations.add_to_cart, priceForCta);
        }
      });
    }

    this.debugLog('Core DOM elements cached');
  }

  /**
   * Renders the bundle selection slots based on `maxSelection`.
   * Each slot is populated by cloning a template, setting its index,
   * and assigning a translated label. Also sets up removal and slider.
   *
   * @param {number} maxSelection - Number of slots to render.
   */
  renderSelectionSlots(maxSelection) {
    if (!maxSelection || maxSelection < 1) {
      this.debugLog('renderSelectionSlots: No selection slots to render');
      return;
    }

    const template = document.getElementById(
      'bundle-builder-selection-template',
    );

    if (!template) {
      this.debugLog('renderSelectionSlots: Selection template not found');
      return;
    }

    const container = template.parentElement;
    if (!container) {
      this.debugLog('renderSelectionSlots: Template has no parent container');
      return;
    }

    // Render each selection slot
    for (let i = 0; i < maxSelection; i++) {
      const cloned = template.content.firstElementChild.cloneNode(true);
      cloned.setAttribute('data-index', i);

      // Label slot using translation (fallback to index + 1)
      const labelEl = cloned.querySelector(
        '.bundle-builder__selector-product-label',
      );
      if (labelEl) {
        const label = `${this.translations.option} ${this.translations[i] || i + 1}`;
        labelEl.textContent = label;
      }

      // Plus / Discount
      const plusSpan = cloned.querySelector(
        '.bundle-builder__selector-product-plus',
      );
      const discountPct = this.slotDiscounts?.[i + 1]; // 1-based
      if (discountPct) {
        plusSpan.textContent = `${this.translations.save} ${discountPct}%`;
        plusSpan.classList.add('has-discount');
      }

      container.appendChild(cloned);
    }

    // Remove the <template> node to clean up DOM
    template.remove();

    // Initialize removal buttons
    const removeButtons = container.querySelectorAll(
      '.bundle-builder__selector-product-remove',
    );
    this.initializeSelectionRemoval(removeButtons);

    // Initialize Swiper slider
    this.initializeSelectionSlider();

    this.debugLog(`renderSelectionSlots: Rendered ${maxSelection} slot(s)`);
  }

  /**
   * Initializes or destroys the selection slider (Swiper) based on screen size.
   * Applies only to mobile viewports (< 1024px). If `maxSelection <= 3`, disables slider.
   */
  initializeSelectionSlider() {
    const selectorProducts = this.querySelector(
      '.bundle-builder__selector-products',
    );

    // Disable slider entirely if only 3 or fewer slots exist
    if (this.maxSelection <= 3) {
      selectorProducts.classList.add('no-slider');
      this.debugLog(
        'initializeSelectionSlider: Skipped slider (3 or fewer selections)',
      );
      return;
    }

    const swiperContainer = selectorProducts.querySelector('.swiper');
    if (!swiperContainer) {
      return;
    }

    const setupSwiper = () => {
      if (this.mediaQuery.matches) {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      } else {
        if (!this.swiper) {
          this.swiper = new window.Swiper(swiperContainer, {
            slidesPerView: 'auto',
            spaceBetween: 8,
            slidesOffsetAfter: 12,
            pagination: {
              el: this.querySelector(
                '.bundle-builder__selector-products .swiper-pagination',
              ),
              clickable: true,
            },
          });
        }
      }
    };

    // Listen for viewport changes and react
    this.mediaQuery.addEventListener('change', setupSwiper);

    // Initial run
    setupSwiper();
  }

  /**
   * Attaches click handlers to all "Add to Bundle" buttons.
   * Handles both adding and removing products, enforcing global and per-step limits.
   */
  initializeProductSelection() {
    this.addToBundleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = event.target.closest('.bundle-builder__product-cta');
        const product = target.closest('.bundle-builder__product');
        const step = product.closest('.bundle-builder__step');
        const stepId = step?.dataset.collectionId;

        if (!stepId || !this.stepQuantities[stepId]) {
          this.debugLog('Missing or untracked stepId for button:', button);
          return;
        }

        const stepData = this.stepQuantities[stepId];
        const quantityWrapper = button.nextElementSibling;
        const hasQuantityInput = !!quantityWrapper?.querySelector('input');
        const productId = target.dataset.productId;

        // If already added and no quantity input, interpret as removal click
        if (button.classList.contains('added') && !hasQuantityInput) {
          this.debugLog('Removing product via ATC re-click:', productId);
          const selectionBox = this.getLastMatchingProduct(productId);
          this.handleProductRemoval(selectionBox);
          return;
        }

        // Step-level limit (only enforced if max is explicitly set)
        if (
          typeof stepData.max === 'number' &&
          stepData.current >= stepData.max
        ) {
          this.debugLog(
            `[BundleBuilder] Step ${stepId} max reached (${stepData.current}/${stepData.max})`,
          );
          return;
        }

        // Global bundle limit
        const totalCurrent = Object.values(this.stepQuantities).reduce(
          (acc, step) => acc + step.current,
          0,
        );
        if (this.maxSelection && totalCurrent >= this.maxSelection) {
          this.debugLog('Global maxSelection reached');
          return;
        }

        // Visually mark button as added
        button.classList.add('added');

        // Increment quantity input if available
        if (hasQuantityInput) {
          const quantityInput = quantityWrapper.querySelector('input');
          const currentValue = parseInt(quantityInput.value, 10) || 0;
          quantityInput.value = currentValue + 1;
        }

        // Pull product visuals and metadata
        const image = product.querySelector(
          '.bundle-builder__product-image img',
        );
        const title = product.querySelector(
          '.bundle-builder__product-title',
        )?.textContent;

        // Use next available selection slot
        const selectionBox = this.querySelector(
          '.bundle-builder__selector-product.is-empty',
        );
        if (!selectionBox) {
          this.debugLog(
            'No available selection box found for product:',
            productId,
          );
          return;
        }

        stepData.current += 1;

        const variantId = target.dataset.variantId;
        const productTitle = target.dataset.productTitle;

        let sellingPlans = target.dataset.sellingPlans;
        if (sellingPlans) {
          sellingPlans = JSON.parse(sellingPlans);
        }

        // Check for existing selection
        const existingSelection = this.bundle.selections.find(
          (selection) => selection.externalProductId === productId,
        );

        if (existingSelection) {
          // If it exists, increase the quantity
          existingSelection.quantity += 1;
        } else {
          // If it doesn't exist, add a new selection object
          this.bundle.selections.push({
            collectionId: step.dataset.collectionId,
            externalProductId: productId,
            externalVariantId: variantId,
            quantity: 1,
            sellingPlan: sellingPlans,
            title: productTitle,
          });
        }

        // Update the slot UI
        selectionBox.dataset.productId = productId;
        this.updateSelectionBox(selectionBox, image, title);

        // Slide swiper to this slot
        const idx = parseInt(selectionBox.dataset.index, 10);
        if (this.swiper && !isNaN(idx) && !this.mediaQuery.matches) {
          this.swiper.slideTo(idx, 300);
        }

        // Refresh buttons & bundle action
        this.updateStepButtonState();
        this.updateBundleActionButton();

        // Trigger modal
        this.emitNthModalIfChanged();

        this.debugLog('Added product:', {
          productId,
          variantId,
          stepId,
          totalCurrent,
          stepCurrent: stepData.current,
        });
      });
    });
  }

  /**
   * Attaches click listeners to all "Remove" buttons in selection slots.
   * When clicked, triggers removal of the associated product from the bundle.
   *
   * @param {NodeList} removeButtons - List of "remove" buttons inside selection boxes
   */
  initializeSelectionRemoval(removeButtons) {
    removeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const selectionBox = event.target.closest(
          '.bundle-builder__selector-product',
        );
        this.debugLog(
          'Removing selection from slot:',
          selectionBox.dataset?.productId,
        );

        this.handleProductRemoval(selectionBox);
      });
    });
  }

  /**
   * Given a quantity button (plus or minus), finds its corresponding
   * "Add to Bundle" CTA button in the same product card.
   *
   * @param {HTMLElement} button
   *   The plus/minus button element within a .bundle-builder__product-quantity wrapper.
   * @returns {HTMLButtonElement|null}
   *   The matching .bundle-builder__product-cta button, or null if not found.
   */
  getAddToCartButton(button) {
    if (!button) {
      return null;
    }

    // Walk up to the CTA wrapper, then find the actual CTA button
    const wrapper = button.closest('.bundle-builder__product-cta-wrapper');
    if (!wrapper) {
      return null;
    }

    const cta = wrapper.querySelector('.bundle-builder__product-cta');
    if (!cta) {
      return null;
    }

    return cta;
  }

  /**
   * Returns the most recently filled selection slot for the given product ID.
   *
   * @param {string} productId
   *   The externalProductId to match against each slot's data-product-id.
   * @returns {HTMLElement|null}
   *   The last '.bundle-builder__selector-product' element matching that ID,
   *   or null if none are found.
   */
  getLastMatchingProduct(productId) {
    // build selector string once
    const slotSelector = `.bundle-builder__selector-product[data-product-id="${productId}"]`;

    // grab all matching slots
    const slots = this.querySelectorAll(slotSelector);
    if (slots.length === 0) {
      this.debugLog('[getLastMatchingProduct] No matching slot for', productId);
      return null;
    }

    // return the last one (most recently added)
    return slots[slots.length - 1];
  }

  /**
   * Sets up handlers for the quantity increment ('+') and decrement ('–') buttons.
   * - The '+' button enforces per-item, per-step, and global limits before triggering an ATC click.
   * - The '–' button delegates to handleProductRemoval().
   */
  initializeQuantityControls() {
    // Handler for each "+" button
    this.productIncreaseQtyButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        if (button.disabled) {
          return;
        }

        // Get the associated input and enforce its max value
        const quantityWrapper = button.closest(
          '.bundle-builder__product-quantity',
        );
        const quantityInput = quantityWrapper?.querySelector('input');

        // Enforce per-item max here
        if (quantityInput) {
          const current = parseInt(quantityInput.value, 10) || 0;
          const max = parseInt(quantityInput.max, 10) || Infinity;
          if (current >= max) {
            this.debugLog(
              `[BundleBuilder] Reached per-item max (${max}) for`,
              quantityInput,
            );
            return;
          }
        }

        // Enforce per-step limit
        const atcButton = this.getAddToCartButton(button);
        const step = button.closest('.bundle-builder__step');
        const stepId = step?.dataset.collectionId;

        if (!stepId || !this.stepQuantities[stepId]) {
          return;
        }

        const stepData = this.stepQuantities[stepId];

        // Only block if step max is defined and reached
        if (
          typeof stepData.max === 'number' &&
          stepData.current >= stepData.max
        ) {
          this.debugLog(
            `[BundleBuilder] Step ${stepId} max reached (${stepData.current}/${stepData.max})`,
          );
          return;
        }

        // Enforce global bundle limit
        const totalCurrent = Object.values(this.stepQuantities).reduce(
          (acc, s) => acc + s.current,
          0,
        );
        if (this.maxSelection && totalCurrent >= this.maxSelection) {
          this.debugLog(
            `[BundleBuilder] Global limit reached (${totalCurrent}/${this.maxSelection})`,
          );
          return;
        }

        // Delegate to the Add-to-Bundle click logic
        if (atcButton) {
          atcButton.click();
        }
      });
    });

    // Handler for each "-" button
    this.productDecreaseQtyButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const atcButton = this.getAddToCartButton(button);
        const productId = atcButton.dataset.productId;
        const activeProduct = this.getLastMatchingProduct(productId);

        this.handleProductRemoval(activeProduct);
      });
    });
  }

  /**
   * Enables or disables Add-to-Bundle and quantity '+' buttons
   * based on per-step limits, global limit, and per-item max.
   */
  updateStepButtonState() {
    // total items selected across all steps
    const totalSelected = Object.values(this.stepQuantities).reduce(
      (acc, step) => acc + step.current,
      0,
    );

    // whether we've hit the overall bundle max
    const isGlobalFull =
      this.maxSelection && totalSelected >= this.maxSelection;

    // iterate each step to apply its own limits
    Object.entries(this.stepQuantities).forEach(([stepId, stepData]) => {
      const stepEl = this.querySelector(
        `.bundle-builder__step[data-collection-id="${stepId}"]`,
      );
      if (!stepEl) return;

      // check if this step's selection count has reached its max
      const isStepFull =
        typeof stepData.max === 'number' && stepData.current >= stepData.max;
      const disable = isStepFull || isGlobalFull;

      // disable/enable Add-to-Bundle buttons
      const atcButtons = stepEl.querySelectorAll(
        '.bundle-builder__product-cta',
      );
      atcButtons.forEach((button) => {
        const isAdded = button.classList.contains('added');
        const isOOS = button.classList.contains('oos');

        if (!isAdded && !isOOS) {
          button.disabled = disable;
          button.classList.toggle('disabled', disable);
        }
      });

      // disable/enable each '+' quantity button
      const plusButtons = stepEl.querySelectorAll(
        '.bundle-builder__product-quantity .plus',
      );
      plusButtons.forEach((plusBtn) => {
        const wrapper = plusBtn.closest('.bundle-builder__product-quantity');
        const input = wrapper?.querySelector('input');
        const current = input ? parseInt(input.value, 10) : 0;
        const max = input ? parseInt(input.max, 10) : Infinity;

        // also disable if the item has reached its per-item max
        const perItemFull = current >= max;
        const shouldDisable = disable || perItemFull;

        plusBtn.disabled = shouldDisable;
        plusBtn.classList.toggle('disabled', shouldDisable);
      });
    });
  }

  /**
   * Handles removal of a product from the bundle selection.
   * Decreases quantity or removes it completely depending on quantity,
   * updates associated UI elements and internal state.
   *
   * @param {HTMLElement} selectionBox - The selection slot DOM element being cleared
   */
  handleProductRemoval(selectionBox) {
    if (!selectionBox) {
      return;
    }

    const productId = selectionBox.dataset.productId;

    // Find the product card associated with the selection
    const productEl = this.querySelector(
      `.bundle-builder__product[data-product-id="${productId}"]`,
    );
    const step = productEl?.closest('.bundle-builder__step');
    const stepId = step?.dataset.collectionId;

    if (!stepId || !this.stepQuantities[stepId]) {
      this.debugLog('Step ID missing or untracked for productId:', productId);
      return;
    }

    // Update selection in internal bundle state
    const index = this.bundle.selections.findIndex(
      (selection) => selection.externalProductId === productId,
    );

    if (index !== -1) {
      const selection = this.bundle.selections[index];

      if (selection.quantity > 1) {
        // If quantity is greater than 1, decrease the quantity
        selection.quantity -= 1;
      } else {
        // If quantity is 1, remove the item completely
        this.bundle.selections.splice(index, 1);
      }

      this.stepQuantities[stepId].current -= 1;
    }

    // Update ATC button and quantity input (if present)
    const atcButton = productEl.querySelector('.bundle-builder__product-cta');

    if (atcButton) {
      const atcWrapper = atcButton.closest(
        '.bundle-builder__product-cta-wrapper',
      );
      const quantityInput = atcWrapper?.querySelector('input');

      if (quantityInput) {
        const currentValue = parseInt(quantityInput.value, 10) || 0;
        const newValue = Math.max(currentValue - 1, 0);
        quantityInput.value = newValue;

        if (newValue === 0) {
          atcButton.classList.remove('added');
        }
      } else {
        // No quantity input case (single quantity products)
        atcButton.classList.remove('added');
      }
    }

    // Reset the selection UI and trigger downstream updates
    this.resetSelectionBox(selectionBox);

    this.reorderSelectionSlots();

    this.updateStepButtonState();
    this.updateBundleActionButton();

    // Trigger modal
    this.emitNthModalIfChanged();

    this.debugLog('Removed productId:', productId, 'from step:', stepId);
    this.debugLog('Updated stepQuantities:', this.stepQuantities);
  }

  /**
   * Rebuilds all selection slots so they're gap-free and ordered by selections.
   * - Clears every slot
   * - Flattens bundle.selections by quantity
   * - Fills slots in sequence
   */
  reorderSelectionSlots() {
    // Grab all slot elements once
    const slots = Array.from(
      this.querySelectorAll('.bundle-builder__selector-product'),
    );

    // Reset each slot to its empty state
    slots.forEach((slot) => this.resetSelectionBox(slot));

    // Expand selections into one entry per unit
    const expanded = this.bundle.selections.flatMap((sel) =>
      Array(sel.quantity).fill(sel),
    );

    // Populate slots with expanded selections
    expanded.forEach((sel, idx) => {
      const slot = slots[idx];
      if (!slot) {
        // no slot available for this item
        return;
      }

      // Clone the product image
      const productEl = this.querySelector(
        `.bundle-builder__product[data-product-id="${sel.externalProductId}"]`,
      );
      const imgSrc = productEl?.querySelector('img')?.src;
      if (imgSrc) {
        const img = document.createElement('img');
        img.src = imgSrc;
        img.alt = sel.title;
        slot
          .querySelector('.bundle-builder__selector-product-image')
          ?.appendChild(img);
      }

      // Set title text
      const labelEl = slot.querySelector(
        '.bundle-builder__selector-product-label',
      );
      if (labelEl) {
        labelEl.textContent = sel.title;
      }

      // Mark slot filled
      slot.classList.remove('is-empty');
      slot.dataset.productId = sel.externalProductId;
    });

    this.debugLog(
      '[reorderSelectionSlots] Slots now:',
      expanded.map((s) => s.externalProductId),
    );
  }

  /**
   * Shows an error message in the bundle builder’s cart error area.
   * If the error element doesn’t exist, logs a warning.
   *
   * @param {string} message  The error text to display.
   */
  displayCartError(message) {
    let errorEl = this.querySelector('.bundle-builder__cart-error');

    errorEl.textContent = message;
  }

  /**
   * Clears any existing cart error message.
   * Hides the error container if present.
   */
  clearCartError() {
    const errorEl = this.querySelector('.bundle-builder__cart-error');
    if (errorEl) {
      errorEl.innerHTML = '';
    }
  }

  /**
   * Updates the 'Add to Cart' button's text, disabled state, and validation.
   * - Computes how many more selections are needed.
   * - If dynamic pricing: recalculates bundle price.
   * - When selection is complete, runs validation and enables/disables the CTA.
   */
  async updateBundleActionButton() {
    const totalMax = this.minSelection;
    const totalCurrent = Object.values(this.stepQuantities).reduce(
      (acc, step) => acc + step.current,
      0,
    );

    const remaining = totalMax - totalCurrent;
    const isComplete = remaining === 0 || remaining < 0;

    const text = isComplete
      ? this.translations.add_to_cart
      : `${this.translations.select} ${remaining} ${
          remaining === 1 ? this.translations.option : this.translations.options
        }`;

    this.debugLog('Update cart button text:', {
      totalCurrent,
      remaining,
      isComplete,
    });

    // When incomplete, keep text only (no price). When complete, we set price after validation.
    if (!isComplete) {
      this.setCartButtonText(text, null);
    }

    // If dynamic pricing, recalc total
    let oneTimeCents = 0;
    if (this.priceRuleType === 'dynamic') {
      oneTimeCents = this.calculateDynamicBundlePrice();
    } else {
      oneTimeCents = this.calculateFixedCtaPrices();
    }

    // Enable or disable the CTA
    if (isComplete) {
      // Run bundle validation before enabling
      const validationResult = await this.handleBundleValidation();
      this.clearCartError();

      if (validationResult === true) {
        this.selectorWrapper.classList.add('is-valid');
        this.cartBtn.removeAttribute('disabled');

        // If it's a fixed-price bundle, init Afterpay once here
        if (
          this.priceRuleType === 'fixed' &&
          this.hasAfterPay &&
          !this.afterpayInitialized
        ) {
          this.afterpayInitialized = true;
          this.initAfterpay(this.productPrice);
        }

        // init Afterpay on every dynamic validation
        if (this.priceRuleType === 'dynamic' && this.hasAfterPay) {
          this.initAfterpay(oneTimeCents);
        }

        // Show price next to "Add to cart"
        const priceForCta = this.getActiveCtaPriceCents();
        this.setCartButtonText(this.translations.add_to_cart, priceForCta);
      } else {
        this.selectorWrapper.classList.remove('is-valid');
        this.cartBtn.setAttribute('disabled', 'true');
        this.displayCartError(validationResult);
        this.setCartButtonText(this.translations.add_to_cart, null);
      }
      this.cartBtn.classList.remove('loading');
    } else {
      // Not enough selections: disable and clear any errors
      this.selectorWrapper.classList.remove('is-valid');
      this.cartBtn.setAttribute('disabled', 'true');
      this.cartBtn.classList.remove('loading');

      this.clearCartError();
    }
  }

  /**
   * Expand selections into one entry per unit.
   */
  _expandSelections() {
    return this.bundle.selections.flatMap((sel) =>
      Array(sel.quantity || 0).fill(sel),
    );
  }

  /**
   * Sum raw total (in cents) from expanded selections.
   */
  _sumRawTotal(expanded) {
    let total = 0;
    expanded.forEach((sel) => {
      const btn = this.querySelector(
        `.bundle-builder__product-cta[data-product-id="${sel.externalProductId}"]`,
      );
      const price = parseInt(btn?.dataset.productPrice, 10) || 0;
      total += price;
    });
    return total;
  }

  /**
   * Highest slot discount (%) for current item count.
   */
  getMaxSlotDiscountPct(itemCount) {
    if (!this.slotDiscounts) return 0;
    let maxPct = 0;
    for (const [slotStr, pct] of Object.entries(this.slotDiscounts)) {
      const n = parseInt(slotStr, 10);
      if (!Number.isNaN(n) && n <= itemCount) {
        maxPct = Math.max(maxPct, Number(pct) || 0);
      }
    }
    return maxPct;
  }

  /**
   * Apply slot discount (%) to a cents value.
   */
  _applySlotDiscount(cents, slotPct) {
    return Math.round(cents * (1 - slotPct / 100));
  }

  /**
   * Apply subscription % to a cents value.
   */
  _applySubscriptionDiscount(cents, subPct) {
    return Math.round(cents * (1 - (Number(subPct) || 0) / 100));
  }

  /**
   * Update both OTP/Sub UI price displays (if present).
   */
  _updatePriceDisplays(oneTimeCents, subscriptionCents) {
    if (this.subscriptionOneTimeDisplay) {
      this.subscriptionOneTimeDisplay.textContent = `$${(oneTimeCents / 100).toFixed(2)}`;
    }
    if (this.subscriptionSubDisplay) {
      this.subscriptionSubDisplay.textContent = `$${(subscriptionCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Cache for CTA usage.
   */
  _cacheCtaPrices(oneTimeCents, subscriptionCents) {
    this.lastOneTimeCents = oneTimeCents;
    this.lastSubscriptionCents = subscriptionCents;
  }

  /**
   * Recalculates dynamic bundle totals and updates the UI.
   * Returns the final one-time price in cents so we can init Afterpay later.
   */
  calculateDynamicBundlePrice() {
    const expanded = this._expandSelections();
    const itemCount = expanded.length;
    const rawTotal = this._sumRawTotal(expanded);

    // slot discount threshold
    const slotPct = this.getMaxSlotDiscountPct(itemCount);

    // One-time: slot discount only
    const oneTimeCents = this._applySlotDiscount(rawTotal, slotPct);

    // Subscription: per-item sub% first, then slot discount
    const subPct = this.subscriptionDiscount;
    const subAfterPct = expanded.reduce((sum, sel) => {
      const btn = this.querySelector(
        `.bundle-builder__product-cta[data-product-id="${sel.externalProductId}"]`,
      );
      const price = parseInt(btn?.dataset.productPrice, 10) || 0;
      return sum + this._applySubscriptionDiscount(price, subPct);
    }, 0);
    const subscriptionCents = this._applySlotDiscount(subAfterPct, slotPct);

    // Update UI + cache
    this._updatePriceDisplays(oneTimeCents, subscriptionCents);
    this._cacheCtaPrices(oneTimeCents, subscriptionCents);

    this.debugLog('calculateDynamicBundlePrice:', {
      rawTotal: `$${(rawTotal / 100).toFixed(2)}`,
      maxSlotDiscount: `${slotPct}%`,
      oneTime: `$${(oneTimeCents / 100).toFixed(2)}`,
      subscriptionDiscountPct: `${subPct}%`,
      subscriptionFinal: `$${(subscriptionCents / 100).toFixed(2)}`,
      items: itemCount,
    });

    return oneTimeCents;
  }

  /**
   * Computes CTA prices for FIXED bundles applying:
   *  - slot discount to the base product price (OTP)
   *  - subscription % to base, then slot discount (SUB)
   * Updates caches and UI; returns oneTimeCents.
   */
  calculateFixedCtaPrices() {
    const base = Number(this.productPrice || 0);

    // count selected units (sum quantities)
    const itemCount = this.bundle.selections.reduce(
      (sum, sel) => sum + (sel.quantity || 0),
      0,
    );

    // slot discount from thresholds
    const slotPct = this.getMaxSlotDiscountPct(itemCount);

    // OTP: slot discount on base
    const oneTimeCents = this._applySlotDiscount(base, slotPct);

    // SUB: apply subscription % to base, then slot discount
    const subPct = this.subscriptionDiscount;
    const afterSub = this._applySubscriptionDiscount(base, subPct);
    const subscriptionCents = this._applySlotDiscount(afterSub, slotPct);

    // Update UI + cache
    this._updatePriceDisplays(oneTimeCents, subscriptionCents);
    this._cacheCtaPrices(oneTimeCents, subscriptionCents);

    this.debugLog('calculateFixedCtaPrices:', {
      base: `$${(base / 100).toFixed(2)}`,
      items: itemCount,
      slotDiscountPct: `${slotPct}%`,
      oneTime: `$${(oneTimeCents / 100).toFixed(2)}`,
      subscriptionPct: `${subPct}%`,
      subscriptionFinal: `$${(subscriptionCents / 100).toFixed(2)}`,
    });

    return oneTimeCents;
  }

  /**
   * Validates the current bundle against Recharge rules.
   * - For fixed bundles: runs validateBundle()
   * - For dynamic bundles: runs validateDynamicBundle()
   * - Always runs validateBundleSelection() afterward
   *
   * @returns {Promise<true|string>}
   *   `true` if valid, otherwise the error message string.
   */
  async handleBundleValidation() {
    const { bundle, cartBtn, priceRuleType } = this;
    this.cartBtn.classList.add('loading');

    try {
      if (priceRuleType === 'fixed') {
        const result = await window.recharge.bundle.validateBundle(bundle);
        if (result !== true) {
          this.debugLog('[Validation] fixed bundle error:', result);
          return result;
        }
      } else {
        const result =
          await window.recharge.bundle.validateDynamicBundle(bundle);
        if (result !== true) {
          this.debugLog('[Validation] dynamic bundle error:', result);
          return result;
        }
      }

      // Unified call to validateBundleSelection
      const { valid, error } =
        await window.recharge.bundle.validateBundleSelection(bundle);

      if (!valid) {
        this.debugLog('[Validation] bundle selection error:', error);
        return error || 'Invalid bundle selection';
      }

      return true;
    } catch (err) {
      this.debugLog('[Validation] unexpected error:', err);
      return 'An unexpected validation error occurred.';
    } finally {
      cartBtn.classList.remove('loading'); // clear spinner
    }
  }

  /**
   * Reads the currently selected selling plan from the UI.
   * Returns `{ sellingPlanId, sellingPlanName }` or `{ null, null }`.
   */
  _resolveActiveSellingPlan() {
    if (!this.subscriptionOptions) {
      return { sellingPlanId: null, sellingPlanName: null };
    }

    const isSubChecked = this.subscriptionOptions.querySelector(
      "input[type='radio'][data-radio-type='subscription_purchase']:checked",
    );

    if (!isSubChecked) {
      return { sellingPlanId: null, sellingPlanName: null };
    }

    const activePlan = this.subscriptionOptions.querySelector(
      '.product-purchasing-allocations option:checked',
    );

    if (!activePlan) {
      return { sellingPlanId: null, sellingPlanName: null };
    }

    return {
      sellingPlanId: activePlan.dataset.sellingPlanId || null,
      sellingPlanName: activePlan.dataset.sellingPlanName || null,
    };
  }

  /**
   * Syncs each selection.sellingPlan to match the active plan name.
   * - If name provided and found in the selection's plan list -> set to its ID
   * - Otherwise remove selection.sellingPlan
   */
  _syncSelectionSellingPlans(sellingPlanName) {
    this.bundle.selections.forEach((selection) => {
      // If no plan name is active, remove.
      if (!sellingPlanName) {
        delete selection.sellingPlan;
        return;
      }

      // If selection still has an array of plans, find the matching one by name.
      if (Array.isArray(selection.sellingPlan)) {
        const matchedPlan = selection.sellingPlan.find(
          (plan) => plan?.name === sellingPlanName,
        );
        if (matchedPlan?.id) {
          selection.sellingPlan = matchedPlan.id; // store just the ID
        } else {
          delete selection.sellingPlan;
        }
      } else {
        // If it's already an ID or something else and doesn't match the active name, clear it.
        // (This keeps things consistent if the user switches plans.)
        delete selection.sellingPlan;
      }
    });
  }

  /**
   * Handles Add-to-Cart clicks
   * preserving promise chains for both fixed and dynamic bundles.
   *
   * @param {Event} event
   */
  async handleCartButtonClick(event) {
    event.preventDefault();
    this.clearCartError();
    this.cartBtn.classList.add('loading');
    let sellingPlanId = null; // The parent's selling plan ID.
    let sellingPlanName = null; // The parent's selling plan name.

    ({ sellingPlanId, sellingPlanName } = this._resolveActiveSellingPlan());
    this._syncSelectionSellingPlans(sellingPlanName);

    if (this.priceRuleType === 'fixed') {
      this.debugLog('[Cart] Processing fixed bundle...', this.bundle);
      this.cartBtn.classList.add('loading');

      window.recharge.bundle
        .getBundleId(this.bundle)
        .then((bundleId) => {
          this.debugLog('[Cart] Received bundleId:', bundleId);

          const contents = this.bundle.selections
            .map((item) =>
              item.quantity > 1
                ? `${item.quantity}x ${item.title}`
                : item.title,
            )
            .join(', ');

          const item = {
            id: this.bundle.externalVariantId,
            quantity: 1,
            properties: { Contents: contents, _rb_id: bundleId },
          };

          if (typeof sellingPlanId !== 'undefined' && sellingPlanId) {
            item.selling_plan = sellingPlanId;
          }

          return this.addItems([item]);
        })
        .then(({ ok, response }) => {
          if (ok) {
            this.debugLog('[Cart] Bundle added successfully');
            this.clearAfterAdd();
          } else {
            this.displayCartError('Unable to add bundle to cart.');
            this.debugLog('[Cart] Failed to add bundle to cart:', response);
          }
        })
        .catch((error) => {
          this.displayCartError('Something went wrong while adding to cart.');
          this.debugLog('[Cart] Error during bundle add process:', error);
        })
        .finally(() => {
          this.cartBtn.classList.remove('loading');
        });
    }

    if (this.priceRuleType === 'dynamic') {
      this.cartBtn.classList.add('loading');

      const dynamicBundleItems = window.recharge.bundle.getDynamicBundleItems(
        this.bundle,
        this.productHandle,
      );

      // The parent product must be included with this bundle and associated via custom line item properties.
      dynamicBundleItems.forEach((item) => {
        // Group the bundle items with pre-existing support for bundled line items.
        item.properties._group = item.properties._rc_bundle;
        // Prevent this line item from being displayed.
        item.properties._is_bundle_item = true;
        // Viusually indicate that it's part of a bundle by name.
        item.properties.Bundle = this.productTitle;

        // Include basic data about its available selling plans due to character limit.
        // Have to assume they're all listed in the same order for frequency.
        const availablePlans = document.querySelector(
          `.bundle-builder__product-cta[data-variant-id="${item.id}"][data-selling-plans]`,
        )?.dataset.sellingPlans;

        if (availablePlans) {
          item.properties._selling_plans = JSON.parse(availablePlans)
            .map((plan) => plan.id)
            .join(',');
        }
      });

      // Include the parent variant with the same grouping.
      dynamicBundleItems.push({
        id: this.variantId,
        quantity: 1,
        properties: {
          _group: dynamicBundleItems[0].properties._rc_bundle,
          _rc_bundle: dynamicBundleItems[0].properties._rc_bundle,
        },
        selling_plan: sellingPlanId || null,
      });

      this.addItems(dynamicBundleItems)
        .then(({ ok, response }) => {
          if (ok) {
            this.debugLog('[Cart] Bundle added successfully');
            this.clearAfterAdd();
          } else {
            this.displayCartError('Unable to add bundle to cart.');
            this.debugLog(
              '[Cart] Failed to add dynamic bundle to cart:',
              response,
            );
          }
        })
        .catch((error) => {
          this.displayCartError('Something went wrong while adding to cart.');
          console.error(error);
        })
        .finally(() => {
          this.cartBtn.classList.remove('loading');
        });
    }
  }

  /**
   * Adds items to the cart using the theme's cart API if available,
   * otherwise falls back to Shopify's AJAX cart API.
   *
   * This method ensures a consistent return shape for both paths,
   * resolving to an object with an `ok` boolean (and `response` if using fetch).
   *
   * @param {Array<Object>} items - An array of cart line items to add.
   * Each item should include at minimum:
   *   - {number} id         - Variant ID.
   *   - {number} quantity   - Quantity to add.
   *   - {Object} [properties] - Optional custom line item properties.
   *   - {number} [selling_plan] - Optional selling plan ID.
   *
   * @returns {Promise<{ ok: boolean, response?: Response }>}
   * Resolves with:
   *   - {boolean} ok - Whether the add-to-cart action succeeded.
   *   - {Response} [response] - The fetch Response object (only present when using fallback).
   */
  addItems(items) {
    if (window.cart && typeof window.cart.addItems === 'function') {
      // Most implementations resolve on success or throw on error
      return Promise.resolve(window.cart.addItems(items)).then(() => ({
        ok: true,
      }));
    }

    // Fallback to Shopify AJAX API
    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    }).then((res) => ({ ok: res.ok, response: res }));
  }

  /**
   * Clear internal bundle state and reset UI after a successful add.
   */
  clearAfterAdd() {
    // Reset internal state
    Object.keys(this.stepQuantities || {}).forEach((stepId) => {
      if (this.stepQuantities[stepId]) this.stepQuantities[stepId].current = 0;
    });
    this.bundle.selections = [];
    this.lastTotalCount = 0; // allow future nth-modal emits

    // Reset selection slots
    this.querySelectorAll('.bundle-builder__selector-product').forEach((slot) =>
      this.resetSelectionBox(slot),
    );

    // Reset product CTAs and quantity inputs
    this.querySelectorAll('.bundle-builder__product-cta.added').forEach((btn) =>
      btn.classList.remove('added'),
    );

    this.querySelectorAll('.bundle-builder__product-quantity input').forEach(
      (input) => {
        const next =
          input.min && !Number.isNaN(Number(input.min)) ? Number(input.min) : 0;
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      },
    );

    // Clear errors and refresh controls/CTA
    this.clearCartError();
    this.updateStepButtonState();
    this.updateBundleActionButton();

    // Afterpay cleanup
    this.afterpayInitialized = false;
    this.clearAfterpayWidget?.();
  }

  /**
   * Fills a selection slot with the given product image and title.
   *
   * @param {HTMLElement} slot The slot element to populate.
   * @param {HTMLImageElement} productImage The original product image to clone.
   * @param {string} title The product title to display.
   */
  updateSelectionBox(slot, productImage, title) {
    if (!slot) {
      this.debugLog('[updateSelectionBox] No slot provided');
      return;
    }

    // Mark slot as filled
    slot.classList.remove('is-empty');

    // Append cloned product image
    const imageWrapper = slot.querySelector(
      '.bundle-builder__selector-product-image',
    );
    if (imageWrapper && productImage) {
      const imgClone = productImage.cloneNode(true);
      imageWrapper.appendChild(imgClone);
    }

    const labelEl = slot.querySelector(
      '.bundle-builder__selector-product-label',
    );

    if (labelEl && title) {
      labelEl.textContent = title;
    }

    const removeButton = slot.querySelector(
      '.bundle-builder__selector-product-remove',
    );

    if (removeButton) {
      removeButton.setAttribute(
        'aria-label',
        this.translations.click_to_remove_selection.replace(
          '{{ product }}',
          title,
        ),
      );
    }
  }

  /**
   * Clears a selection slot back to its empty state.
   * - Removes product assignment
   * - Deletes any cloned images
   * - Restores the default label text
   *
   * @param {HTMLElement} slot  The slot element to reset.
   */
  resetSelectionBox(slot) {
    if (!slot) {
      this.debugLog('[resetSelectionBox] No slot provided to reset');
      return;
    }

    const idx = parseInt(slot.dataset.index, 10);

    // Clear assigned product
    delete slot.dataset.productId;
    slot.classList.add('is-empty');

    // Remove any product image
    const img = slot.querySelector(
      '.bundle-builder__selector-product-image img',
    );
    if (img) {
      img.remove();
    }

    // Restore default label text
    const labelEl = slot.querySelector(
      '.bundle-builder__selector-product-label',
    );
    if (labelEl) {
      labelEl.textContent = `${this.translations.option} ${this.translations[idx] || idx + 1}`;
    }

    const removeButton = slot.querySelector(
      '.bundle-builder__selector-product-remove',
    );

    if (removeButton) {
      removeButton.removeAttribute('aria-label');
    }
  }

  /**
   * Moves the bundle selector in or out of the sidebar based on viewport width.
   * - On desktop (>= 1024px): appends selector into the inner wrapper.
   * - On mobile (< 1024px): moves selector below the header in the main content.
   *
   * Uses the `this.mediaQuery` (defined in constructor) to listen for breakpoint changes.
   */
  setupResponsiveSelectorPositioning() {
    const selector = this.querySelector('.bundle-builder__selector');
    const header = this.querySelector('.bundle-builder__header');
    const main = this.querySelector('.bundle-builder__main');
    const wrapper = this.querySelector('.bundle-builder__inner');

    if (!selector || !header || !main || !wrapper) {
      return;
    }

    /**
     * Moves the selector based on the current `matches` state of mediaQuery.
     * @param {{ matches: boolean }} eOrMq  Either a MediaQueryListEvent or the MediaQueryList itself.
     */
    const updatePosition = (e) => {
      const isDesktop = e.matches;
      if (isDesktop) {
        wrapper.appendChild(selector);
      } else {
        main.insertBefore(selector, header.nextSibling);
      }
    };

    // Initial placement
    updatePosition(this.mediaQuery);

    // Re-run on breakpoint crossovers
    this.mediaQuery.addEventListener('change', updatePosition);
  }

  /**
   * Make .bundle-builder__selector-products sticky on mobile
   * when its top scrolls off-screen, using IntersectionObserver
   */
  setupMobileOptionSticky() {
    const selector = this.querySelector('.bundle-builder__selector-products');

    // Sentinel and spacer elements
    const sentinel = this.querySelector('.bundle-builder__selector-sentinel');
    const spacer = this.querySelector('.bundle-builder__selector-spacer');

    // Swiper
    const swiper = selector.querySelector('.swiper');

    // Measure selector height once
    const selHeight = selector.getBoundingClientRect().height;

    const setSticky = (on) => {
      selector.classList.toggle('sticky', on);
      if (spacer) {
        // Use swiper's natural height (measure before sticky toggles elsewhere)
        spacer.style.height = on ? `${swiper.offsetHeight}px` : '0px';
      }
    };

    // Create the IntersectionObserver
    const io = new IntersectionObserver(
      ([entry]) => {
        if (this.mediaQuery.matches) {
          setSticky(false);
          return;
        }
        // Toggle sticky when sentinel scrolls out of view
        setSticky(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${selHeight}px 0px 0px 0px`,
      },
    );

    // Always observe on initial load if we're on mobile
    if (!this.mediaQuery.matches) {
      io.observe(sentinel);
    }

    // Rewire observer on breakpoint changes
    this.mediaQuery.addEventListener('change', (e) => {
      if (e.matches) {
        // Desktop - stop observing and remove sticky
        io.disconnect();
        setSticky(false);
      } else {
        // Mobile - start observing again
        io.observe(sentinel);
      }
    });
  }

  /**
   * Logs debug messages when debugging is enabled.
   *
   * @param  {...any} args  Values to log.
   */
  debugLog(...args) {
    if (this.debug) {
      console.log('[BundleBuilder]', ...args);
    }
  }

  /**
   * Returns true if subscription purchase option is currently selected.
   */
  isSubscriptionSelected() {
    if (!this.subscriptionOptions) {
      return false;
    }
    const subRadio = this.subscriptionOptions.querySelector(
      "input[type='radio'][data-radio-type='subscription_purchase']",
    );
    return !!(subRadio && subRadio.checked);
  }

  /**
   * Format cents to "$X.YY".
   */
  formatCents(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  /**
   * Set CTA label. If 'priceCents' is provided, append a span with the price.
   */
  setCartButtonText(label, priceCents = null) {
    if (!this.cartBtnText) {
      return;
    }
    if (priceCents == null) {
      this.cartBtnText.textContent = label;
      return;
    }

    this.cartBtnText.innerHTML = `${label} <span class="bundle-builder__selector-atc-price">- ${this.formatCents(priceCents)}</span>`;
  }

  /**
   * Resolve the price (in cents) to show on the CTA right now.
   * - Fixed: productPrice for OTP; apply subscription % for Sub.
   * - Dynamic: use last computed oneTime or subscription values.
   */
  getActiveCtaPriceCents() {
    return this.isSubscriptionSelected()
      ? this.lastSubscriptionCents ?? 0
      : this.lastOneTimeCents ?? 0;
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
